// 「直感に従う」: 入口 → 提案 → ワクワク判定
import React, { useMemo, useState } from 'react';
import { ENTRIES, DONE_MESSAGE, BODY_OPTIONS, getSuggestions, getReason, getBodyGap, EntryType, Elem, BodyReaction } from './suggestions';
import { insertChoice, updateChoice } from './dailyChoices';
import CoinToss from './CoinToss';
import { getNearbyLuckyDirection } from './nearby';
import { NearbyCard, PlaceRecord } from './NearbyCard';

type Props = {
  dayStarName: string;          // 今日の日盤の中宮星（例: 一白水星）
  dayElem: Elem;                // その五行
  dayStars: number[];           // getStarsFor(日盤中宮星)
  dayBranchIdx: number;         // getDayBranchIndex(今日)
  starName: (s: number) => string;
  honmeiStar?: number | null;
  honmeiName?: string | null;   // 本命星名（プロフィール未登録なら null）
  honmeiRel?: string | null;    // getStarRel(本命星, 日盤中宮星)
  isPremium: boolean;
  authUser: any;
  onUnlock: () => void;
};

type Step = 'entry' | 'suggest' | 'body' | 'done' | 'coin';

const C = {
  title: '#78350f',
  accent: '#d97706',
  sub: '#6b7280',
  card: { background: 'white', borderRadius: '16px', border: '1px solid #fde68a', padding: '20px 16px', marginBottom: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' } as React.CSSProperties,
};

const bigBtn = (bg: string, color = 'white', border = 'none'): React.CSSProperties => ({
  width: '100%', minHeight: '56px', padding: '14px 16px', borderRadius: '14px', border, background: bg, color,
  fontSize: '18px', fontWeight: 'bold', fontFamily: 'serif', letterSpacing: '1px', cursor: 'pointer',
});

const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: C.sub, fontSize: '14px', cursor: 'pointer', padding: '10px', textDecoration: 'underline',
};

export default function IntuitionTab({ dayStarName, dayElem, dayStars, dayBranchIdx, starName, honmeiStar, honmeiName, honmeiRel, isPremium, authUser, onUnlock }: Props) {
  const [step, setStep] = useState<Step>('entry');
  const [entry, setEntry] = useState<EntryType | null>(null);
  const [idx, setIdx] = useState(0);
  const [showReason, setShowReason] = useState(false);
  // 「ちょっと違う」と答えた直前の提案と、その行の id（自由記述をそこへ書き足す）
  const [prevRowId, setPrevRowId] = useState<string | null>(null);
  const [feeling, setFeeling] = useState('');
  const [feelingSaved, setFeelingSaved] = useState(false);
  // 直前の判定と、その行の id・身体の反応
  const [lastExcited, setLastExcited] = useState(false);
  const [rowId, setRowId] = useState<string | null>(null);
  const [body, setBody] = useState<BodyReaction | null>(null);

  const canSave = !!(isPremium && authUser?.id);
  const list = useMemo(() => (entry ? getSuggestions(entry, dayElem, new Date()) : []), [entry, dayElem]);
  const suggestion = list[idx];
  const exhausted = entry !== null && idx >= list.length;
  // 近場の吉方位は「どこかへ行きたい」かつ有料のときだけ使う
  const nearby = useMemo(
    () => (honmeiStar ? getNearbyLuckyDirection(dayStars, honmeiStar, dayBranchIdx) : null),
    [dayStars, honmeiStar, dayBranchIdx]);
  const nearbyDirection = entry === 'travel' && isPremium && nearby?.kind === 'ok' ? nearby.direction : null;

  const reset = () => {
    setStep('entry'); setEntry(null); setIdx(0); setShowReason(false);
    setPrevRowId(null); setFeeling(''); setFeelingSaved(false);
    setRowId(null); setBody(null);
  };

  const pickEntry = (e: EntryType) => { setEntry(e); setIdx(0); setStep('suggest'); window.scrollTo({ top: 0 }); };

  // 書きかけの自由記述があれば、直前の「ちょっと違う」の行に残す
  const flushFeeling = async () => {
    const t = feeling.trim();
    if (canSave && prevRowId && t && !feelingSaved) await updateChoice(prevRowId, { free_text: t });
  };

  // 判定のあと、身体の反応を聞く
  const judge = async (excited: boolean) => {
    if (!entry || !suggestion) return;
    await flushFeeling();
    const id = canSave ? await insertChoice(authUser.id, { entry_type: entry, suggestion, excited, direction: nearbyDirection }) : null;
    setShowReason(false); setFeeling(''); setFeelingSaved(false);
    setLastExcited(excited); setRowId(id); setBody(null);
    setStep('body'); window.scrollTo({ top: 0 });
  };

  const pickBody = async (b: BodyReaction) => {
    setBody(b);
    if (canSave && rowId) await updateChoice(rowId, { body_reaction: b });
    // 食い違いがあれば一言見せてから進む。なければそのまま進む
    if (!getBodyGap(lastExcited, b)) proceed();
  };

  const proceed = () => {
    if (lastExcited) { setPrevRowId(null); setStep('done'); }
    else { setPrevRowId(rowId); setIdx(i => i + 1); setStep('suggest'); }
    window.scrollTo({ top: 0 });
  };

  const saveFeeling = async () => { await flushFeeling(); setFeelingSaved(true); };

  return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: '6px 2px 24px', animation: 'fadeIn 0.3s' }}>
      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
        <div style={{ fontSize: '22px', fontWeight: 900, fontFamily: 'serif', color: C.title }}>直感に従う</div>
        <div style={{ fontSize: '14px', color: C.sub, marginTop: '4px' }}>今日の気：{dayStarName}（{dayElem}）</div>
      </div>

      {step === 'entry' && (
        <div style={C.card}>
          <div style={{ fontSize: '17px', fontWeight: 'bold', color: C.title, textAlign: 'center', marginBottom: '14px' }}>今、何をしたい気分？</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {ENTRIES.map(e => (
              <button key={e.id} onClick={() => pickEntry(e.id)}
                style={{ ...bigBtn('#fffbeb', C.title, '1px solid #fde68a'), minHeight: '84px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                <span style={{ fontSize: '26px' }}>{e.icon}</span>
                <span style={{ fontSize: '16px' }}>{e.label}</span>
              </button>
            ))}
          </div>
          <button onClick={() => { setStep('coin'); window.scrollTo({ top: 0 }); }}
            style={{ ...bigBtn('white', C.sub, '1px dashed #d1d5db'), marginTop: '12px', fontSize: '16px' }}>🪙 二択で迷ったらコイントス</button>
          <div style={{ textAlign: 'center' }}>
            <button onClick={() => window.dispatchEvent(new CustomEvent('navigate-tab', { detail: 'reflect' }))} style={linkBtn}>これまでの記録を振り返る</button>
          </div>
        </div>
      )}

      {step === 'coin' && <CoinToss canSave={canSave} userId={authUser?.id} onBack={reset} />}

      {step === 'suggest' && entry && (
        <>
          {entry === 'travel' && (
            <NearbyCard isPremium={isPremium} hasHonmei={!!honmeiStar} nearby={nearby} starName={starName} honmeiName={honmeiName} onUnlock={onUnlock} />
          )}
          {!exhausted ? (
            <div style={C.card}>
              <div style={{ fontSize: '14px', color: C.sub, textAlign: 'center' }}>今日はこちらが合いそうです</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', fontFamily: 'serif', color: C.title, textAlign: 'center', margin: '14px 0 20px', lineHeight: 1.5 }}>{suggestion}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button onClick={() => judge(true)} style={bigBtn('linear-gradient(135deg,#f59e0b,#d97706)')}>ワクワクする</button>
                <button onClick={() => judge(false)} style={bigBtn('white', C.sub, '1px solid #e5e7eb')}>ちょっと違う</button>
              </div>
              <div style={{ textAlign: 'center', marginTop: '10px' }}>
                <button onClick={() => setShowReason(s => !s)} style={linkBtn}>{showReason ? '閉じる' : 'なぜこの提案？'}</button>
              </div>
              {showReason && (isPremium ? (
                <div style={{ background: '#fffbeb', borderRadius: '10px', padding: '12px 14px', fontSize: '14px', color: '#78350f', lineHeight: 1.8 }}>
                  {getReason({ dayStarName, elem: dayElem, index: idx, honmeiName, honmeiRel }).map((l, i) => <p key={i} style={{ marginBottom: '4px' }}>{l}</p>)}
                </div>
              ) : (
                <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '12px 14px', fontSize: '14px', color: C.sub, lineHeight: 1.7, textAlign: 'center' }}>
                  今日の気からの見立ての理由は、プレミアムで読めます。
                  <div><button onClick={onUnlock} style={{ ...linkBtn, color: C.accent }}>プレミアムについて見る</button></div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ ...C.card, textAlign: 'center' }}>
              <div style={{ fontSize: '17px', fontWeight: 'bold', color: C.title, marginBottom: '8px' }}>今日はどれもピンとこないみたい</div>
              <div style={{ fontSize: '14px', color: C.sub, lineHeight: 1.7, marginBottom: '16px' }}>それも大事なサインかもしれません。<br />本当はどうしたいか、少し感じてみてね。</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button onClick={async () => { await flushFeeling(); setIdx(0); setPrevRowId(null); setFeeling(''); }} style={bigBtn('white', C.title, '1px solid #fde68a')}>最初から見る</button>
                <button onClick={async () => { await flushFeeling(); reset(); }} style={bigBtn('white', C.sub, '1px solid #e5e7eb')}>別の気分を選ぶ</button>
                <button onClick={async () => { await flushFeeling(); reset(); setStep('coin'); }} style={linkBtn}>二択で迷っているならコイントス</button>
              </div>
            </div>
          )}

          {idx > 0 && (
            <div style={C.card}>
              <div style={{ fontSize: '15px', fontWeight: 'bold', color: C.title, marginBottom: '8px' }}>今の本当の気持ちは？</div>
              <textarea value={feeling} onChange={e => { setFeeling(e.target.value); setFeelingSaved(false); }} rows={3}
                placeholder="書かなくても大丈夫です"
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '16px', fontFamily: 'inherit', resize: 'vertical', background: '#f9fafb' }} />
              {canSave ? (
                <div style={{ textAlign: 'right', marginTop: '6px' }}>
                  {feelingSaved
                    ? <span style={{ fontSize: '13px', color: '#16a34a' }}>書きとめました</span>
                    : <button onClick={saveFeeling} disabled={!feeling.trim()} style={{ ...linkBtn, padding: '4px', color: feeling.trim() ? C.accent : '#d1d5db' }}>書きとめる</button>}
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>気持ちの記録は、プレミアムで残せます。</div>
              )}
            </div>
          )}

          {!exhausted && <div style={{ textAlign: 'center' }}><button onClick={async () => { await flushFeeling(); reset(); }} style={linkBtn}>別の気分を選ぶ</button></div>}
        </>
      )}

      {step === 'body' && entry && (() => {
        const gap = body ? getBodyGap(lastExcited, body) : null;
        return (
          <div style={C.card}>
            <div style={{ fontSize: '14px', color: C.sub, textAlign: 'center' }}>「{suggestion}」に{lastExcited ? 'ワクワク' : 'ちょっと違う'}</div>
            <div style={{ fontSize: '19px', fontWeight: 'bold', fontFamily: 'serif', color: C.title, textAlign: 'center', margin: '10px 0 16px' }}>今、身体はどんな感じ？</div>
            {!gap ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {BODY_OPTIONS.map(o => (
                    <button key={o.id} onClick={() => pickBody(o.id)}
                      style={{ ...bigBtn('#fffbeb', C.title, '1px solid #fde68a'), minHeight: '76px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <span style={{ fontSize: '24px' }}>{o.icon}</span>
                      <span style={{ fontSize: '16px' }}>{o.label}</span>
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center', marginTop: '10px' }}>身体の答えはヒントのひとつ。正解はありません。</div>
                <div style={{ textAlign: 'center' }}><button onClick={proceed} style={linkBtn}>よくわからない</button></div>
              </>
            ) : (
              <>
                <div style={{ fontSize: '18px', fontWeight: 'bold', fontFamily: 'serif', color: C.accent, textAlign: 'center', marginBottom: '8px' }}>どちらが本当かな</div>
                <div style={{ background: '#fffbeb', borderRadius: '10px', padding: '12px 14px', fontSize: '15px', color: '#78350f', lineHeight: 1.8, marginBottom: '16px' }}>{gap}</div>
                <button onClick={proceed} style={bigBtn('linear-gradient(135deg,#f59e0b,#d97706)')}>{lastExcited ? 'うん、進む' : '次の提案を見る'}</button>
                {!lastExcited && (
                  <div style={{ textAlign: 'center' }}>
                    <button onClick={() => { setStep('suggest'); setBody(null); }} style={linkBtn}>やっぱりこの提案をもう一度見る</button>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })()}

      {step === 'done' && entry && (
        <div style={{ ...C.card, textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '6px' }}>✨</div>
          <div style={{ fontSize: '20px', fontWeight: 'bold', fontFamily: 'serif', color: C.title, marginBottom: '8px' }}>{DONE_MESSAGE[entry]}</div>
          <div style={{ fontSize: '14px', color: C.sub, marginBottom: '18px' }}>
            {canSave ? '今日の記録に残しました。' : '記録を残して振り返るのは、プレミアムで使えます。'}
          </div>
          <button onClick={reset} style={bigBtn('white', C.title, '1px solid #fde68a')}>もう一度選ぶ</button>
          {entry === 'travel' && canSave && rowId && <PlaceRecord rowId={rowId} />}
        </div>
      )}

      <div style={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center', lineHeight: 1.7, marginTop: '18px' }}>
        気の話は参考であり、医療・介護・法律の判断の代わりにはしません。
      </div>
    </div>
  );
}
